import { FC, useEffect } from 'react'
import { createPortal } from 'react-dom'
import Link from 'next/link'
import { useTranslation } from 'react-i18next'
import MenuTrackOrder from '@components/icons/MenuTrackOrder'
import MenuFindStore from '@components/icons/MenuFindStore'
import MenuFaq from '@components/icons/MenuFaq'
import MenuChat from '@components/icons/MenuChat'
import MenuHelpCenter from '@components/icons/MenuHelpCenter'
import MenuClose from '@components/icons/MenuClose'
import { FOOTER_URLS } from '@components/common/Footer/footer-config'
import s from './HelpCenterPanel.module.scss'

const TRACK_ORDER_URL =
    'https://ayuda.casaideas.cl/centro-ayuda/seguimiento-pedido/buscar'

type ZendeskCommand = (...args: unknown[]) => void

const openEvaChat = (): void => {
    const zE = (window as Window & { zE?: ZendeskCommand }).zE
    if (typeof zE !== 'function') return
    try {
        zE('messenger', 'open')
    } catch {
        zE('webWidget', 'open')
    }
}

interface Props {
    open: boolean
    onClose: () => void
}

const HelpCenterPanel: FC<Props> = ({ open, onClose }) => {
    const { t } = useTranslation('common')

    useEffect(() => {
        if (!open) return
        const previous = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onClose()
        }
        window.addEventListener('keydown', onKey)
        return () => {
            document.body.style.overflow = previous
            window.removeEventListener('keydown', onKey)
        }
    }, [open, onClose])

    if (!open || typeof document === 'undefined') return null

    return createPortal(
        <div className={s.root}>
            <button
                type="button"
                className={s.overlay}
                aria-label={t('Close panel')}
                onClick={onClose}
            />
            <div
                className={s.panel}
                role="dialog"
                aria-modal="true"
                aria-labelledby="help-center-title"
            >
                <header className={s.header}>
                    <h2 id="help-center-title" className={s.title}>
                        {t('Help center', { defaultValue: 'Centro de ayuda' })}
                    </h2>
                    <button
                        type="button"
                        className={s.close}
                        aria-label={t('Close panel')}
                        onClick={onClose}
                    >
                        <MenuClose />
                    </button>
                </header>
                <nav className={s.list}>
                    <Link
                        href={TRACK_ORDER_URL}
                        rel="noreferrer"
                        target="_blank"
                        onClick={onClose}
                    >
                        <MenuTrackOrder />
                        {t('footer|menu track order')}
                    </Link>
                    <Link
                        href={FOOTER_URLS.findStore}
                        rel="noreferrer"
                        target="_blank"
                        onClick={onClose}
                    >
                        <MenuFindStore />
                        {t('footer|menu find store')}
                    </Link>
                    <Link
                        href={FOOTER_URLS.helpCenter}
                        rel="noreferrer"
                        target="_blank"
                        onClick={onClose}
                    >
                        <MenuFaq />
                        {t('footer|menu faq')}
                    </Link>
                    <button
                        type="button"
                        onClick={() => {
                            openEvaChat()
                            onClose()
                        }}
                    >
                        <MenuChat />
                        {t('footer|menu chat')}
                    </button>
                    <Link
                        href={FOOTER_URLS.helpCenter}
                        rel="noreferrer"
                        target="_blank"
                        onClick={onClose}
                    >
                        <MenuHelpCenter />
                        {t('footer|menu help center')}
                    </Link>
                </nav>
            </div>
        </div>,
        document.body
    )
}

export default HelpCenterPanel
